import { IconButton, Stack, Typography, Popover, css, Button, Tooltip, Popper } from '@mui/material';
import CircleIcon from '@mui/icons-material/Circle';
import { useCallback, useMemo, useRef, useState } from 'react';
import { HexColorPicker } from 'react-colorful';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { FormContainer, TextFieldElement, useForm } from 'react-hook-form-mui';
import DoneIcon from '@mui/icons-material/Done';
import CloseIcon from '@mui/icons-material/Close';

import { FrequencySpectrumList } from '../models/types/FrequencySpectrumList';
import { FrequencySpectrumArea } from '../models/types/FrequencySpectrumArea';
import { useSeismicStore } from '../stores/useSeismicStore';
import { getAmplitude } from '../utils/calculateScreenValuesUtils';

export type FrequencySpectrumChartLegendProps = {
    freqSpectrumList: FrequencySpectrumList,
    onChangeColor: (color: string) => void,
    onRemove: () => void,
    onUpdateLimits: (limits: FrequencySpectrumArea) => void,
};

export const FrequencySpectrumChartLegend = ({ freqSpectrumList, onChangeColor, onRemove, onUpdateLimits } : FrequencySpectrumChartLegendProps) => {
    const colorBtnRef = useRef<HTMLButtonElement|null>(null);
    const [ colorPopoverOpened, setColorPopoverOpened ] = useState(false);
    const [ updateFormAnchor, setUpdateFormAnchor ] = useState<HTMLButtonElement|null>(null);

    const styles = useMemo(() => css({
        '& *': {
            color: freqSpectrumList.color
        }
    }), [freqSpectrumList.color]);

    const [ saveHelpAnchor, setSaveHelpAnchor ] = useState<HTMLButtonElement|null>(null);
    const [ closeHelpAnchor, setCloseHelpAnchor ] = useState<HTMLButtonElement|null>(null);
    const calculator = useSeismicStore(state => state.calculator);
    const surveyMetadata = useSeismicStore(state => state.surveyMetadata);
    const scaleY = useSeismicStore(state => state.scale.y);
    const sampleInterval = useMemo(() => !!surveyMetadata && !!calculator ? calculator.calculateSampleInterval(surveyMetadata) : 1, [calculator, surveyMetadata]);
    const getTime = useCallback((sample: number) => Math.abs(parseInt(getAmplitude(sample, scaleY, sampleInterval))), [sampleInterval, scaleY]);
    const [startTime, endTime] = useMemo(() => [getTime(freqSpectrumList.startSample), getTime(freqSpectrumList.endSample)], [freqSpectrumList.endSample, freqSpectrumList.startSample, getTime]);

    const updateFormContext = useForm<FrequencySpectrumArea>({
        defaultValues: {
            startTrace: freqSpectrumList.startTrace,
            endTrace: freqSpectrumList.endTrace,
            startTime: getTime(freqSpectrumList.startSample),
            endTime: getTime(freqSpectrumList.endSample),
        },
    });

    const [ watchedStartTrace, watchedStartTime ] = updateFormContext.watch(['startTrace', 'startTime']);

    return (
        <Stack direction='row' alignItems='CENTER' css={styles}>
            <Stack direction='row' alignItems={'center'}>
                <IconButton ref={colorBtnRef} onClick={() => setColorPopoverOpened(!colorPopoverOpened)} size='small'><CircleIcon/></IconButton>
                <Popover anchorEl={colorBtnRef.current} open={colorPopoverOpened} onClose={() => setColorPopoverOpened(false)}>
                    <HexColorPicker color={freqSpectrumList.color} onChange={onChangeColor}/>
                </Popover>
            </Stack>
            <IconButton onClick={onRemove}><DeleteIcon/></IconButton>
            <IconButton onClick={(evt) => setUpdateFormAnchor(evt.currentTarget)}><EditIcon/></IconButton>
            <Typography variant='body2'> Traces ({freqSpectrumList.startTrace} - {freqSpectrumList.endTrace}) | Time ({startTime} - {endTime}) </Typography>
            <Popover
                anchorEl={updateFormAnchor}
                onClose={() => setUpdateFormAnchor(null)}
                open={!!updateFormAnchor}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center'
                }}
                sx={{zIndex: 999}}
            >
                <FormContainer
                    formContext={updateFormContext}
                    onSuccess={(limits) => {
                        if (limits.endTime < limits.startTime) {
                            [ limits.endTime, limits.startTime ] = [ limits.startTime, limits.endTime ];
                        }
                        if (limits.endTrace < limits.startTrace) {
                            [ limits.endTrace, limits.startTrace ] = [ limits.startTrace, limits.endTrace ];
                        }

                        onUpdateLimits(limits);
                        setUpdateFormAnchor(null);

                    }}
                >
                    <Stack gap={1} padding={'20px'} alignItems='center'>
                        <Stack direction='row' gap={2}>
                            <TextFieldElement type='number' size='small' name='startTrace' label='Start trace' required/>
                            <TextFieldElement type='number' size='small' name='endTrace' label='End trace' required rules={{ validate: (v) => Number(v) !== Number(watchedStartTrace) || 'Must differ from start trace' }}/>
                        </Stack>
                        <Stack direction='row' gap={2}>
                            <TextFieldElement type='number' size='small' name='startTime' label='Start Time' required/>
                            <TextFieldElement type='number' size='small' name='endTime' label='End Time' required rules={{ validate: (v) => Number(v) !== Number(watchedStartTime) || 'Must differ from start time' }}/>
                        </Stack>
                        <div>
                            <Stack direction='row' gap={1}>
                                <IconButton
                                    size='large'
                                    type='submit'
                                    onMouseEnter={(evt) => setSaveHelpAnchor(evt.currentTarget)}
                                    onMouseLeave={() => setSaveHelpAnchor(null)}
                                ><DoneIcon color='primary'/></IconButton>
                                <Popper open={!!saveHelpAnchor} anchorEl={saveHelpAnchor} sx={{zIndex: 9999999999999, backgroundColor: 'rgba(0,0,0,0.75)', borderRadius: '5px', padding: '5px 10px'}}>
                                    <Typography fontWeight='bold' fontSize={'11px'} color='white'>Apply</Typography>
                                </Popper>
                                <IconButton
                                    size='large'
                                    onClick={() => setUpdateFormAnchor(null)}
                                    onMouseEnter={(evt) => setCloseHelpAnchor(evt.currentTarget)}
                                    onMouseLeave={() => setCloseHelpAnchor(null)}
                                >
                                    <CloseIcon color='primary'/>
                                </IconButton>
                                <Popper open={!!closeHelpAnchor} anchorEl={closeHelpAnchor} sx={{zIndex: 9999999999999, backgroundColor: 'rgba(0,0,0,0.75)', borderRadius: '5px', padding: '5px 10px'}}>
                                    <Typography fontWeight='bold' fontSize={'11px'} color='white'>Close</Typography>
                                </Popper>
                            </Stack>
                        </div>
                    </Stack>
                </FormContainer>
            </Popover>
        </Stack>
    );
};